/**
 * A tiny migrator that runs the same drizzle-kit SQL on the server (node-postgres) and on
 * clients (PGlite), including the browser where there is no filesystem: callers pass the
 * SQL files in (read from disk in Node, imported as text by Vite in the browser).
 */
export interface Migration {
  tag: string;
  sql: string;
}

export interface SqlExecutor {
  exec(sql: string): Promise<unknown>;
  query<T>(sql: string, params?: unknown[]): Promise<{ rows: T[] }>;
}

export async function migrate(db: SqlExecutor, migrations: Migration[]): Promise<string[]> {
  await db.exec(`CREATE TABLE IF NOT EXISTS _migrations (
    tag text PRIMARY KEY,
    applied_at timestamptz NOT NULL DEFAULT now()
  )`);
  const done = new Set(
    (await db.query<{ tag: string }>('SELECT tag FROM _migrations')).rows.map((r) => r.tag),
  );
  const applied: string[] = [];
  for (const m of [...migrations].sort((a, b) => a.tag.localeCompare(b.tag))) {
    if (done.has(m.tag)) continue;
    const statements = m.sql
      .split('--> statement-breakpoint')
      .map((s) => s.trim())
      .filter(Boolean);
    await db.exec('BEGIN');
    try {
      for (const s of statements) await db.exec(s);
      await db.query('INSERT INTO _migrations (tag) VALUES ($1)', [m.tag]);
      await db.exec('COMMIT');
    } catch (e) {
      await db.exec('ROLLBACK');
      throw new Error(`Migration ${m.tag} failed: ${(e as Error).message}`);
    }
    applied.push(m.tag);
  }
  return applied;
}

/** Server start-up: shared migrations first, then the server-only ones. */
export async function migrateServer(
  db: SqlExecutor,
  shared: Migration[],
  server: Migration[],
): Promise<string[]> {
  return [...(await migrate(db, shared)), ...(await migrate(db, server))];
}
